const MembershipProcess = () => {
  const processSteps = [
    {
      step: "01",
      title: "Submit Application",
      description: "Complete our comprehensive membership application with your personal information and style preferences.",
      timeline: "5-10 minutes",
      icon: "📝"
    },
    {
      step: "02", 
      title: "Style Assessment",
      description: "Our team reviews your application and style profile to understand your unique preferences and needs.",
      timeline: "1-2 business days",
      icon: "👗"
    },
    {
      step: "03",
      title: "Personal Consultation", 
      description: "Schedule a complimentary consultation with one of our luxury fashion experts to discuss your membership.",
      timeline: "30-45 minutes",
      icon: "☕"
    },
    {
      step: "04",
      title: "Membership Activation",
      description: "Upon approval, your membership is activated and you'll receive your welcome package with exclusive benefits.",
      timeline: "24-48 hours",
      icon: "🎁"
    }
  ]

  const requirements = [
    {
      title: "Eligibility Criteria",
      items: [
        "Minimum age of 21 years",
        "Valid government-issued ID",
        "Proof of income or assets",
        "Professional or personal references"
      ]
    },
    {
      title: "Required Information",
      items: [
        "Personal and contact details",
        "Style preferences and sizing",
        "Fashion history and brands",
        "Lifestyle and occasion needs"
      ]
    },
    {
      title: "Application Review",
      items: [
        "Background verification",
        "Style compatibility assessment",
        "Membership tier recommendation",
        "Personalization planning"
      ]
    }
  ]

  return (
    <section className="py-20 bg-white">
      <div className="container-luxury">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="luxury-heading text-3xl md:text-4xl mb-6 text-luxury-black-950">
            Application Process
          </h2>
          <p className="luxury-body text-lg text-luxury-black-600 max-w-2xl mx-auto">
            Our streamlined application process ensures we understand your unique style 
            preferences and can provide the most personalized luxury experience.
          </p>
        </div>

        {/* Process Steps */}
        <div className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connection Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-8 h-px bg-luxury-gold-300 transform translate-x-4 z-0" />
                )}
                
                <div className="bg-luxury-cream-50 p-6 text-center h-full relative z-10 hover:shadow-luxury transition-shadow duration-300">
                  {/* Step Number */}
                  <div className="w-16 h-16 bg-luxury-gold-500 text-luxury-black-950 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {step.step}
                  </div>
                  
                  {/* Icon */}
                  <div className="text-3xl mb-4">{step.icon}</div>
                  
                  {/* Content */}
                  <h3 className="luxury-subheading text-lg mb-3 text-luxury-black-950">
                    {step.title}
                  </h3>
                  
                  <p className="luxury-body text-sm text-luxury-black-600 mb-4 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <div className="text-xs uppercase tracking-wider text-luxury-gold-600 font-medium">
                    {step.timeline}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {requirements.map((requirement, index) => (
            <div key={index} className="bg-luxury-cream-50 p-8">
              <h3 className="luxury-subheading text-xl mb-6 text-luxury-black-950">
                {requirement.title}
              </h3>
              
              <div className="space-y-3">
                {requirement.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-luxury-gold-500 rounded-full mt-2 flex-shrink-0" />
                    <span className="luxury-body text-sm text-luxury-black-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Timeline Overview */}
        <div className="bg-luxury-black-950 text-white p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="luxury-heading text-2xl mb-6 text-white">
                Expected Timeline
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-luxury-gold-500/20 border border-luxury-gold-400 rounded-full flex items-center justify-center">
                    <span className="text-luxury-gold-400">⏱️</span>
                  </div>
                  <div>
                    <div className="luxury-subheading text-lg text-white">Application Review</div>
                    <div className="text-luxury-cream-200 text-sm">2-3 business days</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-luxury-gold-500/20 border border-luxury-gold-400 rounded-full flex items-center justify-center">
                    <span className="text-luxury-gold-400">📞</span>
                  </div>
                  <div>
                    <div className="luxury-subheading text-lg text-white">Consultation Scheduling</div>
                    <div className="text-luxury-cream-200 text-sm">Within 48 hours</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-luxury-gold-500/20 border border-luxury-gold-400 rounded-full flex items-center justify-center">
                    <span className="text-luxury-gold-400">✅</span>
                  </div>
                  <div>
                    <div className="luxury-subheading text-lg text-white">Membership Activation</div>
                    <div className="text-luxury-cream-200 text-sm">Same day approval</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-luxury-black-900 p-8 border border-luxury-gold-500/20">
              <h4 className="luxury-subheading text-xl mb-4 text-white">
                Fast Track Option
              </h4>
              <p className="luxury-body text-luxury-cream-100 mb-6 leading-relaxed">
                For qualified applicants, we offer expedited processing that can complete 
                the entire application process within 24 hours.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-2">
                  <span className="text-luxury-gold-400 text-sm">✓</span>
                  <span className="text-luxury-cream-200 text-sm">Priority review queue</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-luxury-gold-400 text-sm">✓</span>
                  <span className="text-luxury-cream-200 text-sm">Same-day consultation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-luxury-gold-400 text-sm">✓</span>
                  <span className="text-luxury-cream-200 text-sm">Immediate activation</span>
                </div>
              </div>
              
              <div className="text-sm text-luxury-cream-200">
                Available for Elite and Signature tier applications
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h3 className="luxury-heading text-2xl mb-4 text-luxury-black-950">
            Ready to Begin?
          </h3>
          <p className="luxury-body text-luxury-black-600 mb-8 max-w-2xl mx-auto">
            Start your application today and join our exclusive community of discerning fashion enthusiasts.
          </p>
          <a
            to="#application-form"
            className="btn-luxury-primary"
          >
            Start Your Application
          </a>
        </div>
      </div>
    </section>
  )
}

export default MembershipProcess